import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Box, Cpu, Layers, Gauge, ArrowRight, CheckCircle2 } from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import LightPageHero from '@/components/LightPageHero';

const process = [
  {
    step: '01',
    title: 'Briefing & Design',
    desc: 'Vi diskuterar krav och mått. Därefter skapar vi eller optimerar CAD-modellen med AI-assisterade verktyg.',
  },
  {
    step: '02',
    title: 'Materialval',
    desc: 'PLA, PETG, ABS, TPU eller specialfilament — vi väljer material baserat på funktion och miljö.',
  },
  {
    step: '03',
    title: 'Printning & QC',
    desc: 'Hög-precisionsprintning med noggrann kvalitetskontroll. Toleranser ner till ±0.1 mm.',
  },
  {
    step: '04',
    title: 'Efterbehandling',
    desc: 'Slipning, målning, montering eller ytbehandling — leveransklart till din dörr.',
  },
];

const features = [
  {
    icon: Gauge,
    title: 'Snabb turnaround',
    desc: 'Prototyper inom 24–72 timmar från godkänd design. Ingen lång väntan.',
    gradient: 'from-amber-400 to-orange-500',
    glow: 'rgba(251,146,60,0.3)',
  },
  {
    icon: Layers,
    title: 'Alla material',
    desc: 'PLA, PETG, ABS, ASA, TPU, CF-Nylon och mer. Rätt material för rätt applikation.',
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.3)',
  },
  {
    icon: Cpu,
    title: 'AI-assisterad CAD',
    desc: 'Vi använder AI för att optimera designs för styrka, vikt och materialåtgång.',
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.3)',
  },
  {
    icon: Box,
    title: 'Små & stora serier',
    desc: 'Från enstaka prototyper till serier på hundratals enheter — samma precision.',
    gradient: 'from-violet-500 to-purple-600',
    glow: 'rgba(139,92,246,0.3)',
  },
];

const useCases = [
  'Produktprototyper & proof-of-concept',
  'Maskindelar & reservdelar',
  'Jigs, fixturer & monteringshjälpmedel',
  'Konsumentprodukter & housings',
  'Arkitekturmodeller & mockups',
  'Anpassade förpackningar & inserts',
  'EPA-traktorreservdelar & anpassade delar',
  'Verkstadsspecifika fixturer och mallar',
];


const ThreeDPrinting = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>3D-printing — Från fil till fysisk produkt på 24–72h | Aifinity</title>
        <meta name="description" content="Prototyper, reservdelar och produktionsdelar. AI-assisterad CAD-design. PLA, PETG, ABS, TPU och mer. Kostnadsfri offert inom ett dygn." />
        <meta property="og:url" content="https://aifinity.se/3d-printing" />
        <link rel="canonical" href="https://aifinity.se/3d-printing" />
      </Helmet>
      <CorporateNavbar />

      <LightPageHero
        badge="3D-PRINTING · 24–72H LEVERANS"
        heading={<>Få din produkt producerad<br /><span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>inom 24–72 timmar</span></>}
        subtext="Prototyper, reservdelar och produktionsdelar. Snabbt, precist och utan mellanhänder."
        ctaLabel="Begär offert"
        ctaHref="/contact"
      />

      {/* ── Features ── */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Varför Aifinity</span>
            <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
              Precision möter hastighet
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-white rounded-2xl border border-border-light p-6 transition-all duration-300"
                style={{ boxShadow: '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 8px 24px rgba(99,102,241,0.15), 0 20px 50px ${f.glow}`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)'; }}
              >
                <div className={`inline-flex w-10 h-10 rounded-xl bg-gradient-to-br ${f.gradient} items-center justify-center mb-4 shadow-md`}
                  style={{ boxShadow: `0 4px 12px ${f.glow}` }}>
                  <f.icon className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-[17px] font-bold text-text-primary mb-2">{f.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process ── */}
      <section className="py-24 bg-surface-subtle">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Vår process</span>
            <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
              Så här arbetar vi
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p, i) => (
              <div
                key={p.step}
                className="bg-white rounded-2xl p-6 border border-border-light"
                style={{ boxShadow: '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)' }}
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mb-4 shadow-md">
                  <span className="text-xs font-bold text-white">{p.step}</span>
                </div>
                <h3 className="text-[17px] font-bold text-text-primary mb-2">{p.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases + CTA ── */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">

            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-5">Användningsområden</span>
              <h2 className="text-3xl font-extrabold text-text-primary tracking-tight mb-8">
                Vad vi kan printa åt er
              </h2>
              <ul className="space-y-3.5">
                {useCases.map(u => (
                  <li key={u} className="flex items-start gap-3 text-[15px] text-text-secondary">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                    {u}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="bg-surface-subtle rounded-3xl p-10 border border-border-light"
            >
              <h3 className="text-2xl font-extrabold text-text-primary mb-4">Redo att beställa?</h3>
              <p className="text-text-secondary leading-relaxed mb-6">
                Skicka era filer eller berätta om ert projekt — vi sätter ihop en offert och återkommer inom ett dygn.
              </p>
              <Link to="/contact">
                <button className="group btn-glow relative overflow-hidden w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 text-white font-semibold px-7 py-3.5 rounded-xl text-sm flex items-center justify-center gap-2">
                  Begär offert nu
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
              </Link>
              <p className="text-xs text-text-secondary mt-4 text-center">Kostnadsfri offert · Snabb leverans · Professionellt resultat</p>
            </motion.div>

          </div>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default ThreeDPrinting;
